import React, { ReactNode } from 'react';

import { Control, useWatch } from 'react-hook-form';

import { InputBox, LbInput, LbSelect } from '@infoods/lifebase-ui';

import classes from './InbodyInfoForm.module.scss';
import {
  INBODY_TYPES,
  inbodyTypeOptions,
} from '../../../../shared/constants/inbody-info/InbodyType';
import { CombinedCustomerInfo } from '../../../interfaces/Customer';
import { Mode, Option } from '../../../../general-types';
import { validateWhitespace } from '../../../../shared/utils/reactHookFormUtils';
import { ModeAwareInput } from '../../../../shared/components/form-components';
import { CustomerProfileFormMode } from '../CustomerProfileForm.settings';

type GeneralDataFromProps = {
  mode: CustomerProfileFormMode;
  control: Control<CombinedCustomerInfo>;
  supportedInbodyTypeOptions?: Option[];
  children: ReactNode;
};

const InbodyInfoForm = ({
  mode,
  control,
  supportedInbodyTypeOptions = [],
  children,
}: GeneralDataFromProps) => {
  const generalInputProps = {
    size: 'small',
    variant: 'standard',
  } as const;

  const privateInfoProps = { InputProps: { className: 'private-info' } };
  /** blur only the value, label is not private */
  const privateValueClassName = 'private-info';

  const inbodyType = useWatch({ control, name: 'inbodyInfo.inbodyType' });
  const isInbodyType = inbodyType && inbodyType !== INBODY_TYPES.NON_INBODY;

  // InBody info is editable only on create, profile EDIT shows it read-only
  const fieldMode: Mode = mode === 'CREATE' ? 'EDIT' : 'VIEW';
  const modeAwareProps = { mode: fieldMode, control } as const;

  return (
    <InputBox title="InBody Info" mode={fieldMode}>
      <ModeAwareInput
        {...modeAwareProps}
        name="inbodyInfo.inbodyType"
        label="InBody Type"
        options={inbodyTypeOptions}
        renderEdit={(props) => (
          <LbSelect
            {...generalInputProps}
            {...props}
            options={supportedInbodyTypeOptions}
            rules={{ required: 'User Type is required' }}
          />
        )}
      />
      {isInbodyType && (
        <ModeAwareInput
          {...modeAwareProps}
          name="inbodyInfo.inbodyId"
          label="InBody ID"
          valueClassName={privateValueClassName}
          renderEdit={(props) => (
            <LbInput
              {...generalInputProps}
              {...privateInfoProps}
              {...props}
              rules={{
                required: 'InBody ID is required',
                // null only reaches here for the NON_INBODY branch of the union, where `required` fires first
                validate: {
                  validateWhitespace: (value) => validateWhitespace(value ?? ''),
                },
              }}
            />
          )}
        />
      )}
      {children && <div className={classes.spanAll}>{children}</div>}
    </InputBox>
  );
};

export { InbodyInfoForm };
